import { permissionsEnumSchema } from "@mindlercare/schema";
import ArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import ArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { Typography } from "components/typography";
import { hasPermissions } from "helpers/permissions";
import { useCurrentUser } from "hooks/userHooks";
import { NavigateAction, ToolbarProps } from "react-big-calendar";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Button } from "@mindlercare/ui-web";
import { CalendarActionsMenu } from "../../calendar/CalendarActionsMenu";
import MonthPicker from "../../calendar/monthPicker";
import SelectTemplateButton from "../../calendar/selectTemplateButton";
import { RevisitSlotClipboard } from "./revisitClipboard";
import { addWeeks, getWeek } from "date-fns";
import {
  Container,
  Icon,
  NavigationContainer,
  NavigationLeft,
  ToolbarContainer,
  ToolbarInner,
  ToolbarInnerItem,
} from "./toolbar.styles";
import { SelectedPatientButton } from "./selectedPatientButton";
import { useInvalidateSlots } from "components/booking/hooks/useInvalidateSlots";
import { useWeekDate } from "components/booking/hooks/useWeekDate";

const StyledCalendarSettings = styled.div`
  display: flex;
  align-items: center;
`;

type Navigate = Exclude<NavigateAction, "DATE">;

const getNextWeekIndex = (date: Date, navigate: Navigate) => {
  if (navigate === "TODAY") return date;
  const count = navigate === "PREV" ? -1 : 1;
  return addWeeks(date, count);
};

export const Toolbar = (props: ToolbarProps) => {
  const { t } = useTranslation();

  const { setWeekDate } = useWeekDate();
  const invalidateSlots = useInvalidateSlots();

  const currentWeekNumber = getWeek(props.date);

  const { user } = useCurrentUser();
  const hasClipboardRevisitPermission = hasPermissions(
    user,
    permissionsEnumSchema.enum.CLIPBOARD_REVISITS,
  );

  const handleNavigate = (date: Date, navigate: Navigate) => {
    setWeekDate(getNextWeekIndex(date, navigate));
    props.onNavigate(navigate);
    invalidateSlots();
  };

  return (
    <Container data-testid="toolbarContainer">
      <NavigationContainer>
        <NavigationLeft>
          <StyledCalendarSettings>
            <MonthPicker date={props.date} />
          </StyledCalendarSettings>
        </NavigationLeft>
      </NavigationContainer>
      <ToolbarContainer>
        <ToolbarInner>
          <ToolbarInnerItem>
            <Icon
              onClick={() => handleNavigate(props.date, "PREV")}
              data-testid="calendar-prev"
              id="calendar-prev"
            >
              <ArrowLeft />
            </Icon>
            <Typography insensitive variant="body">{`${t(
              "WEEK",
            )} ${currentWeekNumber}`}</Typography>
            <Icon
              onClick={() => handleNavigate(props.date, "NEXT")}
              data-testid="calendar-next"
              id="calendar-next"
            >
              <ArrowRight />
            </Icon>
          </ToolbarInnerItem>
          <ToolbarInnerItem>
            <Button
              variant="secondary-small"
              onClick={() => handleNavigate(new Date(), "TODAY")}
              testId="calendar-today"
            >
              {t("TODAY")}
            </Button>
          </ToolbarInnerItem>
          <ToolbarInnerItem>
            {hasClipboardRevisitPermission && (
              <ToolbarInner>
                <ToolbarInnerItem>
                  <RevisitSlotClipboard />
                </ToolbarInnerItem>
              </ToolbarInner>
            )}
          </ToolbarInnerItem>
          <ToolbarInnerItem>
            <CalendarActionsMenu />
          </ToolbarInnerItem>
        </ToolbarInner>
        <ToolbarInner>
          <ToolbarInnerItem>
            <SelectedPatientButton />
            <SelectTemplateButton />
          </ToolbarInnerItem>
        </ToolbarInner>
      </ToolbarContainer>
    </Container>
  );
};
